import { useState, useEffect, useCallback, useRef } from 'react';
import { extractApiData, extractApiError } from '../utils/apiClient';

/**
 * Hook for async data fetching with loading/error state management.
 * Replaces the common pattern of useState + useEffect + try/catch/finally.
 *
 * @param {Function} fetchFn - Async function that returns API data (e.g., () => apiGet('/contracts'))
 * @param {object} [options]
 * @param {*} [options.defaultValue=[]] - Default value before data loads
 * @param {boolean} [options.fetchOnMount=true] - Whether to fetch immediately on mount
 * @param {Array} [options.deps=[]] - Additional dependencies that trigger a refetch
 * @param {boolean} [options.extractData=true] - Whether to run response through extractApiData
 * @returns {{ data: *, loading: boolean, error: string|null, reload: Function, setData: Function }}
 */
const useAsyncData = (fetchFn, options = {}) => {
  const {
    defaultValue = [],
    fetchOnMount = true,
    deps = [],
    extractData = true,
  } = options;

  const [data, setData] = useState(defaultValue);
  const [loading, setLoading] = useState(fetchOnMount);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const fetchFnRef = useRef(fetchFn);
  const defaultValueRef = useRef(defaultValue);
  const extractDataRef = useRef(extractData);

  // Keep refs current without triggering re-renders
  fetchFnRef.current = fetchFn;
  defaultValueRef.current = defaultValue;
  extractDataRef.current = extractData;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchFnRef.current();
      if (!mountedRef.current) return;
      setData(extractDataRef.current ? extractApiData(response, defaultValueRef.current) : response);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(extractApiError(err));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (fetchOnMount) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, ...deps]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { data, loading, error, reload: load, setData };
};

export default useAsyncData;
